import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  CheckCircle2,
  Circle,
  AlertCircle,
  FileEdit,
  Eye,
  Layout,
} from "lucide-react";
import {
  getContentPages,
  getPageContent,
  publishPage,
  getPageConfig,
} from "../../../../Services/adminCorporates";
import amplitudeService from "../../../../Services/amplitude";
import { useCorporate } from "../CorporateContext";
import { Button } from "../../../../ui/button";
import { Skeleton } from "../../../../ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../../../../ui/sheet";
import { cn } from "../../../../lib/utils";
import { SECTION_DISPLAY_NAMES } from "./content/schemas";
import PreviewLayout from "./content/PreviewLayout";
import EditableHotspot from "./content/EditableHotspot";
import LayoutEditor from "./content/LayoutEditor";

import HeroEditor from "./content/editors/HeroEditor";
import HeroImageEditor from "./content/editors/HeroImageEditor";
import WhatThisIsEditor from "./content/editors/WhatThisIsEditor";
import WhenToUseEditor from "./content/editors/WhenToUseEditor";
import WhereItFitsEditor from "./content/editors/WhereItFitsEditor";
import PrivateByDesignEditor from "./content/editors/PrivateByDesignEditor";
import ClosingTaglineEditor from "./content/editors/ClosingTaglineEditor";
import FooterEditor from "./content/editors/FooterEditor";
import CtaEditor from "./content/editors/CtaEditor";
import StepLabelsEditor from "./content/editors/StepLabelsEditor";

const EDITOR_BY_KEY = {
  hero_image: HeroImageEditor,
  hero: HeroEditor,
  what_this_is: WhatThisIsEditor,
  when_to_use: WhenToUseEditor,
  where_it_fits: WhereItFitsEditor,
  private_by_design: PrivateByDesignEditor,
  closing_tagline: ClosingTaglineEditor,
  footer: FooterEditor,
  cta: CtaEditor,
  step_labels: StepLabelsEditor,
};

const sectionStatus = (section) => {
  if (!section || section.content == null) return "blank";
  if (!section.is_published) return "draft";
  if (section.has_unpublished_changes) return "unpublished_changes";
  return "published";
};

const STATUS_META = {
  blank: { label: "Not yet authored", Icon: Circle, className: "text-muted-foreground" },
  draft: { label: "Draft", Icon: FileEdit, className: "text-amber-600" },
  published: { label: "Published", Icon: CheckCircle2, className: "text-emerald-600" },
  unpublished_changes: {
    label: "Unpublished changes",
    Icon: AlertCircle,
    className: "text-amber-600",
  },
};

// Normalize whatever shape getPageContent returns into a section-key → record map.
const normalizeSections = (raw) => {
  const map = {};
  if (Array.isArray(raw)) {
    raw.forEach((s) => {
      if (s?.section_key) map[s.section_key] = s;
    });
  } else if (raw && typeof raw === "object") {
    Object.entries(raw).forEach(([k, v]) => {
      if (v && typeof v === "object" && "section_key" in v) {
        map[v.section_key] = v;
      } else {
        map[k] = { section_key: k, content: v };
      }
    });
  }
  return map;
};

const Content = () => {
  const { id } = useParams();
  const { corporate } = useCorporate();

  const [pages, setPages] = useState({});
  const [pageKey, setPageKey] = useState("activation");
  const [sectionsMap, setSectionsMap] = useState({});
  const [activeSectionKey, setActiveSectionKey] = useState(null);
  const [liveOverrides, setLiveOverrides] = useState({});

  const [pagesLoading, setPagesLoading] = useState(true);
  const [sectionsLoading, setSectionsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [publishingAll, setPublishingAll] = useState(false);
  const [publishResult, setPublishResult] = useState(null);

  // Layout config: section ordering + visibility for the current page.
  const [layoutEditorOpen, setLayoutEditorOpen] = useState(false);
  const [savedLayout, setSavedLayout] = useState({
    sectionOrder: [],
    hiddenKeys: [],
  });
  const [previewLayout, setPreviewLayout] = useState({
    sectionOrder: [],
    hiddenKeys: [],
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPagesLoading(true);
      try {
        const data = await getContentPages(id);
        if (cancelled) return;
        const pagesMap = data?.pages || { activation: [] };
        setPages(pagesMap);
        const firstPage = Object.keys(pagesMap)[0] || "activation";
        setPageKey(firstPage);
      } catch (err) {
        if (!cancelled)
          setError(err?.response?.data?.message || err?.message || "Failed to load");
      } finally {
        if (!cancelled) setPagesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const loadPage = useCallback(async () => {
    if (!id || !pageKey) return;
    setSectionsLoading(true);
    setError(null);
    try {
      const [data, config] = await Promise.all([
        getPageContent(id, pageKey),
        getPageConfig(id, pageKey).catch(() => null),
      ]);
      setSectionsMap(normalizeSections(data?.sections));
      setLiveOverrides({});
      const nextLayout = {
        sectionOrder: Array.isArray(config?.section_order)
          ? config.section_order
          : [],
        hiddenKeys: Array.isArray(config?.hidden_section_keys)
          ? config.hidden_section_keys
          : [],
      };
      setSavedLayout(nextLayout);
      setPreviewLayout(nextLayout);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to load page content");
    } finally {
      setSectionsLoading(false);
    }
  }, [id, pageKey]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const handleSectionSaved = (updated) => {
    if (!updated?.section_key) return;
    setSectionsMap((prev) => ({ ...prev, [updated.section_key]: updated }));
    // Clear the live override for the saved section — the saved record now
    // reflects what the admin sees.
    setLiveOverrides((prev) => {
      const next = { ...prev };
      delete next[updated.section_key];
      return next;
    });
  };

  const handlePublishAll = async () => {
    setPublishingAll(true);
    setPublishResult(null);
    try {
      const result = await publishPage(id, pageKey);
      setPublishResult(result);
      amplitudeService.track?.("admin_section_published", {
        corporate_id: id,
        page_key: pageKey,
        bulk: true,
      });
      loadPage();
    } catch (err) {
      setPublishResult({
        error: err?.response?.data?.message || err?.message || "Publish failed",
      });
    } finally {
      setPublishingAll(false);
    }
  };

  const handleLiveChange = useCallback(
    (sectionKey) => (values) => {
      setLiveOverrides((prev) => ({ ...prev, [sectionKey]: values }));
    },
    []
  );

  // Build the section-content map fed to the preview: live overrides win over
  // saved drafts so edits in the panel show up immediately.
  const previewSections = useMemo(() => {
    const result = {};
    const sectionKeys = pages[pageKey] || Object.keys(sectionsMap);
    sectionKeys.forEach((key) => {
      result[key] =
        liveOverrides[key] !== undefined
          ? liveOverrides[key]
          : sectionsMap[key]?.content;
    });
    return result;
  }, [pages, pageKey, sectionsMap, liveOverrides]);

  if (pagesLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const pageOptions = Object.keys(pages || { activation: [] });
  const ActiveEditor = activeSectionKey ? EDITOR_BY_KEY[activeSectionKey] : null;
  const activeSection = activeSectionKey ? sectionsMap[activeSectionKey] : null;
  const activeLabel = activeSectionKey
    ? SECTION_DISPLAY_NAMES[activeSectionKey] || activeSectionKey
    : "";

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-3">
          <Select value={pageKey} onValueChange={setPageKey}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Page" />
            </SelectTrigger>
            <SelectContent>
              {pageOptions.map((key) => (
                <SelectItem key={key} value={key}>
                  {key === "activation" ? "Activation page" : key}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
            <Eye className="h-3.5 w-3.5" />
            Click any section to edit
          </span>
        </div>
        <div className="flex items-center gap-3">
          <StatusLegend />
          {publishResult && (
            <span
              className={cn(
                "text-xs",
                publishResult.error ? "text-destructive" : "text-emerald-600"
              )}
            >
              {publishResult.error ||
                `Published ${publishResult.published_count ?? Object.keys(sectionsMap).length}, skipped ${
                  publishResult.skipped?.length || 0
                }.`}
            </span>
          )}
          <Button
            variant="outline"
            onClick={() => setLayoutEditorOpen(true)}
          >
            <Layout className="h-4 w-4 mr-1.5" />
            Edit layout
          </Button>
          <Button onClick={handlePublishAll} disabled={publishingAll}>
            {publishingAll ? "Publishing…" : "Publish entire page"}
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      {sectionsLoading ? (
        <Skeleton className="h-[600px] w-full" />
      ) : (
        <PreviewLayout
          branding={corporate || {}}
          sections={previewSections}
          sectionOrder={previewLayout.sectionOrder}
          hiddenKeys={previewLayout.hiddenKeys}
          renderSection={(key, node) => (
            <EditableHotspot
              key={key}
              sectionKey={key}
              status={sectionStatus(sectionsMap[key])}
              isActive={activeSectionKey === key}
              onClick={() => setActiveSectionKey(key)}
            >
              {node}
            </EditableHotspot>
          )}
        />
      )}

      <Sheet
        open={layoutEditorOpen}
        onOpenChange={(open) => {
          setLayoutEditorOpen(open);
          if (!open) {
            // Discard any unsaved local layout edits — revert to last saved.
            setPreviewLayout(savedLayout);
          }
        }}
      >
        <SheetContent className="flex flex-col p-0">
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-zinc-200">
            <SheetTitle>Edit layout</SheetTitle>
            <SheetDescription>
              Reorder sections or toggle their visibility on the activation
              page. Changes preview live and only apply to this corporate.
            </SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-5">
            {layoutEditorOpen && (
              <LayoutEditor
                key={`${pageKey}:${layoutEditorOpen}`}
                pageKey={pageKey}
                onLocalChange={(next) => setPreviewLayout(next)}
                onSaved={(next) => {
                  setSavedLayout(next);
                  setPreviewLayout(next);
                }}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>

      <Sheet
        open={!!activeSectionKey && !!ActiveEditor}
        onOpenChange={(open) => {
          if (!open) setActiveSectionKey(null);
        }}
      >
        <SheetContent className="flex flex-col p-0">
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-zinc-200">
            <SheetTitle>{activeLabel}</SheetTitle>
            <SheetDescription>
              Changes preview live on the left. Save draft to keep your work, or
              publish to push it to the public page.
            </SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-5">
            {ActiveEditor && activeSectionKey && (
              <ActiveEditor
                key={`${pageKey}:${activeSectionKey}`}
                section={activeSection}
                onSaved={handleSectionSaved}
                onLiveChange={handleLiveChange(activeSectionKey)}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

const StatusLegend = () => (
  <div className="hidden md:flex items-center gap-3 text-[11px] text-muted-foreground">
    {Object.entries(STATUS_META).map(([key, meta]) => {
      const Icon = meta.Icon;
      return (
        <span key={key} className={cn("inline-flex items-center gap-1", meta.className)}>
          <Icon className="h-3 w-3" />
          {meta.label}
        </span>
      );
    })}
  </div>
);

export default Content;
